'use client';

import Link from 'next/link';
import { Suspense } from 'react';

import Spinner from '@/components/Spinner';
import { Routes } from '@/config/routes';
import { useAuth } from '@/hooks/auth';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from '@tolstoy/ui-library/core';
import TermsAndConditions from '@/components/TermsAndConditions';

export const WebsiteAuth = ({ children }) => {
  const [
    { isLoading, isLoggedIn, error, isSignUp, email, password },
    { signIn, signUp, googleLogin, toggleSignUp, setEmail, setPassword },
  ] = useAuth();

  const handleGoogleLoginClick = () => {
    googleLogin();
  };

  if (isLoading) {
    return <Spinner />;
  }

  if (isLoggedIn) {
    return children;
  }

  return (
    <Suspense fallback={<Spinner />}>
      <div className="flex min-h-screen items-center justify-center">
        <Card className="mx-auto flex max-w-sm flex-col justify-center">
          <CardHeader>
            <CardTitle className="text-2xl">
              {isSignUp ? 'Sign Up' : 'Login'}
            </CardTitle>
            <CardDescription>
              {isSignUp
                ? 'Enter your details below to create your account'
                : 'Enter your email below to login to your account'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="m@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="grid gap-2">
                <div className="flex items-center">
                  <Label htmlFor="password">Password</Label>
                  {!isSignUp && (
                    <Link
                      href={Routes.recoverPassword}
                      className="ml-auto inline-block text-sm underline"
                    >
                      Forgot your password?
                    </Link>
                  )}
                </div>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <TermsAndConditions className="my-2 text-center" />
              <Button
                type="button"
                className="w-full"
                onClick={isSignUp ? signUp : signIn}
              >
                {isSignUp ? 'Sign Up' : 'Login'}
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={handleGoogleLoginClick}
              >
                {isSignUp ? 'Sign Up with Google' : 'Login with Google'}
              </Button>
            </div>
            {error && (
              <div className="mt-4 text-center text-sm text-red-500">
                {error}
              </div>
            )}
            <div className="mt-4 text-center text-sm">
              {isSignUp ? (
                <>
                  Already have an account?{' '}
                  <button onClick={toggleSignUp} className="underline">
                    Login
                  </button>
                </>
              ) : (
                <>
                  Don&apos;t have an account?{' '}
                  <button onClick={toggleSignUp} className="underline">
                    Sign up
                  </button>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </Suspense>
  );
};
